import SearchInput from "./SearchInput.tsx";
import styles from "./SearchComboBox.module.scss";
import {
  ComponentPropsWithoutRef,
  ReactElement,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { clsx } from "clsx";
import {
  autoUpdate,
  ElementProps,
  offset,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
} from "@floating-ui/react";
import { useIntl } from "react-intl";

interface ListItem {
  key: string;
  element?: ReactElement;
  text?: string;
}

interface ComboBoxProps extends ComponentPropsWithoutRef<typeof SearchInput> {
  listItems: ListItem[];
  listTitle?: string;
  onSelectionMade?: (index: number) => void;
}

function getOptionId(index: number) {
  return `ComboBoxOption${index}`;
}

const usePopoverAria = (): ElementProps => {
  const id = useId();
  return useMemo(
    () => ({
      reference: {
        role: "combobox",
        "aria-controls": id,
        autoComplete: "off",
        "aria-autocomplete": "list",
      },
      floating: {
        id,
        role: "listbox",
      },
    }),
    [id]
  );
};

export default function SearchComboBox({
  listItems,
  listTitle,
  className,
  onSelectionMade,
  ...inputProps
}: ComboBoxProps) {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open: isExpanded,
    onOpenChange: setIsExpanded,
    placement: "bottom-start",
    middleware: [
      offset({ mainAxis: 10, crossAxis: 0 }),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight - 16}px`,
          });
        },
      }),
    ],
  });

  const listRef = useRef<HTMLLIElement[]>([]);

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex: highlightedIndex,
    onNavigate: setHighlightedIndex,
    virtual: true,
  });

  const focus = useFocus(context, {
    visibleOnly: false,
  });
  const dismiss = useDismiss(context);
  const aria = usePopoverAria();

  const { getReferenceProps, getFloatingProps } = useInteractions([
    focus,
    dismiss,
    listNavigation,
    aria,
  ]);

  function onItemSelected(index: number) {
    if (inputProps.value === undefined) {
      (refs.reference.current as HTMLInputElement).value =
        listItems[index].text ?? "";
    }
    setIsExpanded(false);
    onSelectionMade?.(index);
  }

  const isShowingContent = isExpanded && listItems.length > 0;

  return (
    // Support handled throughout
    // onKeyDown provided on container to avoid conflict with floating-ui props on input
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={clsx(styles.container, className)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && highlightedIndex !== null) {
          e.preventDefault();
          onItemSelected(highlightedIndex);
        }
      }}
    >
      <SearchInput
        {...inputProps}
        ref={refs.setReference}
        {...getReferenceProps()}
        aria-expanded={isShowingContent}
      />
      {isShowingContent && (
        <div
          className={clsx("vst-popover", styles.popover)}
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          aria-label={intl.formatMessage({
            defaultMessage: "Search Results",
          })}
        >
          <div className="vst-popover-section">
            {listTitle && (
              <h3 className="vst-popover-section-header">{listTitle}</h3>
            )}
            <ul className="vst-popover-link-list">
              {listItems.map((item, i) => (
                // Key handler on container element
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                  // This is complaining about aria-selected, but the implementation
                  // in supernova does not use aria-selected. Not sure what the
                  // discrepancy is.
                  // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                  role="option"
                  key={item.key}
                  id={getOptionId(i)}
                  className={clsx(
                    highlightedIndex === i && "vst-popover-item-active"
                  )}
                  ref={(el) => (listRef.current[i] = el!)}
                  onClick={() => {
                    onItemSelected(i);
                  }}
                >
                  {item.element ?? item.text}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
